import { useMemo } from 'react'
import {
  useNavigate as tanNavigate,
  useRouterState,
  useParams as tanParams,
} from '@tanstack/react-router'

export function useParams() {
  return tanParams({ strict: false })
}

/**
 * Next.js-compatible router shim.
 *
 * IMPORTANT: must return a stable, memoised object across renders so that
 * consumers depending on `router` in `useEffect` / `useCallback` dependency
 * arrays don't re-run their effects on every render and trigger
 * "Maximum update depth exceeded" loops (e.g. RouteGuard.tsx:84 was hitting
 * this — it included `router` in its deps).
 */
export function useRouter() {
  const navigate = tanNavigate()
  return useMemo(
    () => ({
      push: (path: string) => navigate({ to: path as any }),
      replace: (path: string) =>
        navigate({ to: path as any, replace: true }),
      back: () => window.history.back(),
      prefetch: () => {},
      refresh: () => window.location.reload(),
    }),
    [navigate],
  )
}

export function usePathname() {
  return useRouterState({ select: (s) => s.location.pathname })
}

/**
 * Returns a stable URLSearchParams across renders for the same `search`
 * string.  Without memoisation, every render creates a new instance and
 * any consumer using it as an effect dep would re-run on every render.
 */
export function useSearchParams() {
  const search = useRouterState({
    select: (s) => s.location.search as string,
  })
  return useMemo(() => new URLSearchParams(search), [search])
}
